import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

/** Matches GET /new-app/status/table `sortBy` query (Nest StatusTableQueryDto). */
export type StatusTableSortBy =
  | "mark"
  | "classNumber"
  | "typeOfMark"
  | "tmNo"
  | "applDate"
  | "status";

export type StatusTableSortOrder = "ASC" | "DESC";

export type StatusTableListParams = {
  typeOfMark?: string;
  status?: string;
  class?: number;
  page?: number;
  limit?: number;
  module?: string;
  sortBy?: StatusTableSortBy;
  sortOrder?: StatusTableSortOrder;
};

function appendStatusTableQuery(
  query: URLSearchParams,
  params: StatusTableListParams,
  options: { includePagination: boolean }
): void {
  if (params.typeOfMark) query.set("typeOfMark", params.typeOfMark);
  if (params.status) query.set("status", params.status);
  if (params.class != null && !Number.isNaN(params.class)) {
    query.set("class", String(params.class));
  }
  if (options.includePagination) {
    if (params.page != null) query.set("page", String(params.page));
    if (params.limit != null) query.set("limit", String(params.limit));
  }
  if (params.module) query.set("module", params.module);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
}

export interface ExcelStatusChangedRow {
  tmNo: string;
  previousStatus: string | null;
  newStatus: string;
}

export interface ExcelStatusInvalidRow {
  tmNo: string;
  status: string;
}

/** Response from POST /new-app/status/table/upload-excel */
export interface ExcelStatusUploadResult {
  updated: number;
  changed: ExcelStatusChangedRow[];
  notFound: string[];
  invalidStatus: ExcelStatusInvalidRow[];
  excelFileKey: string;
}

/** One row from GET /new-app/status/table */
export interface StatusTableRow {
  id: string;
  leadId: string;
  no: number;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  tmNo: string;
  applDate: string;
  status: string;
  acknowledgementNo?: string | null;
  dateOfFiling?: string | null;
  /** True when status was updated after last client-notify stamp (highlight in UI). */
  changed?: boolean;
}

export interface StatusTableResult {
  rows: StatusTableRow[];
  total: number;
}

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string;
  error?: string;
}

export interface ExcelImportPreviewBlock {
  kind: "changedStatus" | "noChange";
  leadId: string;
  clientName: string | null;
  /** New status for changedStatus blocks; null for noChange. */
  status: string | null;
  tmNos: string[];
  message: string;
}

export interface ExcelImportPreviewResult {
  updatesCount: number;
  changedCount: number;
  notFound: string[];
  invalidStatus: { tmNo: string; status: string }[];
  blocks: ExcelImportPreviewBlock[];
  autoStatusInformDefault: boolean;
}

/**
 * Dry-run: render the WhatsApp messages auto-inform would send for this Excel,
 * without committing any status change or sending anything. Used by the
 * UploadStatusModal "Send for this upload" preview confirmation.
 */
export async function previewStatusExcelMessages(
  file: File,
  signal?: AbortSignal,
): Promise<ExcelImportPreviewResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFetch(`${API_BASE}/new-app/status/table/upload-excel/preview`, {
    method: "POST",
    body: form,
    signal,
  });
  const json = (await res.json()) as
    | ApiSuccessResponse<ExcelImportPreviewResult>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Preview failed";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExcelImportPreviewResult>).data;
}

export type UploadStatusExcelOptions = {
  /**
   * Overrides server AUTO_STATUS_INFORM_ON_EXCEL for this upload only.
   * Omit to use the server default.
   */
  autoInform?: boolean;
};

export async function uploadStatusExcel(
  file: File,
  signal?: AbortSignal,
  options?: UploadStatusExcelOptions
): Promise<ExcelStatusUploadResult> {
  const form = new FormData();
  form.append("file", file);
  if (options?.autoInform === true) {
    form.append("autoInform", "true");
  }
  if (options?.autoInform === false) {
    form.append("autoInform", "false");
  }

  const res = await apiFetch(`${API_BASE}/new-app/status/table/upload-excel`, {
    method: "POST",
    body: form,
    signal,
  });

  const json = (await res.json()) as ApiSuccessResponse<ExcelStatusUploadResult> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Upload failed";
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<ExcelStatusUploadResult>).data;
  return data;
}

export async function updateRowStatus(
  leadId: string,
  tmNo: string,
  status: string,
): Promise<void> {
  const res = await apiFetch(`${API_BASE}/new-app/status/${encodeURIComponent(leadId)}/table`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ applications: [{ tmNo, status }] }),
  });

  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update status";
    throw new Error(message);
  }
}

export async function getStatusTable(
  params?: StatusTableListParams
): Promise<StatusTableResult> {
  const query = new URLSearchParams();
  if (params) {
    appendStatusTableQuery(query, params, { includePagination: true });
  }

  const qs = query.toString();
  const url = qs
    ? `${API_BASE}/new-app/status/table?${qs}`
    : `${API_BASE}/new-app/status/table`;

  const res = await apiFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<StatusTableResult> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load status table";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<StatusTableResult>).data;
}

/**
 * Download Excel for the status table (all rows matching filters/sort; no pagination).
 * Same query semantics as GET /new-app/status/table except page/limit are omitted.
 */
export async function exportStatusTableExcel(
  params: Omit<StatusTableListParams, "page" | "limit">
): Promise<void> {
  const query = new URLSearchParams();
  appendStatusTableQuery(query, params, { includePagination: false });

  const qs = query.toString();
  const url = qs
    ? `${API_BASE}/new-app/status/table/export?${qs}`
    : `${API_BASE}/new-app/status/table/export`;

  const res = await apiFetch(url, { method: "GET" });

  if (!res.ok) {
    let message = "Export failed";
    try {
      const json = (await res.json()) as ApiErrorResponse;
      message = json.message || json.error || message;
    } catch {
      try {
        message = await res.text();
      } catch {
        /* ignore */
      }
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition");
  const match = cd?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) ?? cd?.match(/filename="([^"]+)"/i);
  const fromHeader = match?.[1]?.trim();
  const filename =
    fromHeader && fromHeader.length > 0
      ? decodeURIComponent(fromHeader)
      : `trademark-status-${new Date().toISOString().slice(0, 10)}.xlsx`;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
